import Banner from "components/ui/Banner";
import Button from "components/ui/Button";
import { useTranslation } from "translation/useTranslation";
import { employeeManagementPath } from "helpers/employeeDeeplink";

const conflictCopy = {
  duplicate_employee_number: {
    title: ["user_import.conflict.duplicate.title", "Doppelte Personalnummer"],
    body: ["user_import.conflict.duplicate.body", "Diese Personalnummer kommt mehrfach in der Importdatei vor. Korrigieren Sie die Datei und importieren Sie die betroffenen Zeilen erneut."],
  },
  employee_archived: {
    title: ["user_import.conflict.archived.title", "Archivierter Beschäftigtendatensatz"],
    body: ["user_import.conflict.archived.body", "Die Personalnummer gehört zu einem archivierten Datensatz. Klären Sie zuerst den Beschäftigtenstatus außerhalb des Imports."],
  },
  employee_exited: {
    title: ["user_import.conflict.exited.title", "Ausgeschiedene Person"],
    body: ["user_import.conflict.exited.body", "Die Personalnummer gehört zu einer ausgeschiedenen Person. Klären Sie zuerst den Beschäftigtenstatus außerhalb des Imports."],
  },
  identity_name_missing: {
    title: ["user_import.conflict.name_missing.title", "Name kann nicht geprüft werden"],
    body: ["user_import.conflict.name_missing.body", "Vor- oder Nachname fehlt. Ergänzen Sie die Datei und importieren Sie die Zeile erneut."],
  },
  identity_name_mismatch: {
    title: ["user_import.conflict.name_mismatch.title", "Name stimmt nicht überein"],
    body: ["user_import.conflict.name_mismatch.body", "Prüfen Sie sorgfältig, ob die importierte Zeile wirklich zu dieser Person gehört."],
  },
};

const displayValue = value => value === null || value === undefined || value === "" ? "–" : value;

export const emailConflictEmployeePath = conflict => {
  if (!conflict?.employee_hash_id || !conflict?.employee_number) return null;

  return employeeManagementPath({ staffId: conflict.employee_number, employee: conflict.employee_hash_id });
};

export function EmailConflictEmployeeLink({ conflict, onClick }) {
  const path = emailConflictEmployeePath(conflict);
  const name = conflict?.employee_name?.trim();
  const label = name || `Personalnummer ${conflict?.employee_number}`;

  if (!path) return <span>{label}</span>;

  return <a href={path} target="_blank" rel="noopener noreferrer" onClick={onClick}>{label}</a>;
}

function IdentityCard({ title, identity, testId }) {
  const { editableTranslate } = useTranslation();

  return (
    <div className="col-md-6 mb-3 mb-md-0">
      <div className="border rounded bg-light p-3 h-100" data-testid={testId}>
        <strong className="d-block mb-3">{title}</strong>
        <dl className="mb-0">
          <div className="d-flex justify-content-between mb-2">
            <dt className="text-muted mr-3">{editableTranslate("field.employee_number", "Personalnummer")}</dt>
            <dd className="mb-0 text-right">{displayValue(identity?.employee_number)}</dd>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <dt className="text-muted mr-3">{editableTranslate("field.first_name", "Vorname")}</dt>
            <dd className="mb-0 text-right">{displayValue(identity?.first_name)}</dd>
          </div>
          <div className="d-flex justify-content-between">
            <dt className="text-muted mr-3">{editableTranslate("field.last_name", "Nachname")}</dt>
            <dd className="mb-0 text-right">{displayValue(identity?.last_name)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

export default function EmployeeImportConflictReview({ record, capabilities = {}, resolving = false, onResolve }) {
  const { editableTranslate } = useTranslation();

  if (!record || record.status !== "conflicted") return null;

  const identityConflict = record.meta?.identity_conflict || {};
  const emailConflict = record.meta?.email_conflict;
  const code = identityConflict.code || "conflict";
  const copy = conflictCopy[code] || {
    title: ["user_import.conflict.generic.title", "Importkonflikt"],
    body: ["user_import.conflict.generic.body", "Dieser Datensatz konnte nicht automatisch verarbeitet werden."],
  };
  const resolved = !!record.conflict_resolution;
  const canIgnore = capabilities.can_ignore === true;
  const canConfirmUpdate = capabilities.can_update === true
    && identityConflict.resolvable === true
    && code === "identity_name_mismatch";

  return (
    <div className="mt-3" data-testid={`conflict-review-${record.hash_id}`}>
      <Banner intent={resolved ? "success" : "warning"} title={emailConflict
        ? editableTranslate("user_import.conflict.email.title", "E-Mail bereits vergeben")
        : editableTranslate(...copy.title)}>
        {emailConflict
          ? <span><EmailConflictEmployeeLink conflict={emailConflict} /> {emailConflict.employee_name && `(Personalnummer ${emailConflict.employee_number})`}</span>
          : <span>{editableTranslate(...copy.body)}</span>}
        {resolved && (
          <span className="d-block mt-1 font-weight-bold" data-testid="conflict-resolution-status">
            {record.conflict_resolution === "update"
              ? editableTranslate("user_import.conflict.resolved_update", "Die importierten Daten wurden bestätigt und angewendet.")
              : editableTranslate("user_import.conflict.resolved_ignore", "Der Konflikt wurde geprüft und ignoriert.")}
          </span>
        )}
      </Banner>

      {code === "identity_name_mismatch" && (
        <div className="row mt-3" data-testid="identity-comparison">
          <IdentityCard
            title={editableTranslate("user_import.conflict.existing_employee", "Vorhandene Person")}
            identity={identityConflict.existing_employee}
            testId="existing-identity"
          />
          <IdentityCard
            title={editableTranslate("user_import.conflict.imported_employee", "Importierte Zeile")}
            identity={record.imported_identity}
            testId="imported-identity"
          />
        </div>
      )}

      {!resolved && (canIgnore || canConfirmUpdate) && (
        <div className="d-flex flex-wrap justify-content-end mt-3">
          {canIgnore && (
            <Button
              variant="secondary"
              size="sm"
              className="mr-2 mb-2"
              disabled={resolving}
              onClick={() => onResolve(record.hash_id, "ignore")}
              data-testid="ignore-conflict-button"
            >
              {editableTranslate("user_import.conflict.ignore", "Als geprüft markieren")}
            </Button>
          )}
          {canConfirmUpdate && (
            <Button
              variant="primary"
              size="sm"
              className="mb-2"
              disabled={resolving}
              onClick={() => onResolve(record.hash_id, "update")}
              data-testid="confirm-imported-data-button"
            >
              {editableTranslate("user_import.conflict.confirm_update", "Importierte Daten übernehmen")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
